import { useState } from 'react'
import { Copy, Check, Boxes, Sprout } from 'lucide-react'
import Card from '@/components/base/Card'
import { LinkButton } from '@/components/base/Button'
import ServerStatus from './ServerStatus'
import type { ServerInfo } from '@/config/serverConfig'
import { useLanguage } from '@/localization'

interface ServerCardProps {
  server: ServerInfo
  kind: 'vanilla' | 'modded'
  /** Variant B blocky styling. */
  blocky?: boolean
}

export default function ServerCard({ server, kind, blocky = false }: ServerCardProps) {
  const [copied, setCopied] = useState(false)
  const { t } = useLanguage()
  const isVanilla = kind === 'vanilla'

  const copyHost = async () => {
    try {
      await navigator.clipboard.writeText(server.host)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard unavailable — no-op */
    }
  }

  const accent = isVanilla ? 'grass' : 'sky'
  const Icon = isVanilla ? Sprout : Boxes

  return (
    <Card variant={accent} blocky={blocky} className="flex flex-col h-full">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div
            className={[
              'flex items-center justify-center w-12 h-12 text-white shadow-block',
              blocky ? 'rounded-none' : 'rounded-xl',
              isVanilla ? 'bg-grass-dark' : 'bg-sky-deep',
            ].join(' ')}
          >
            <Icon size={26} />
          </div>
          <div>
            <h3
              className={[
                'leading-tight',
                blocky ? 'font-pixel text-sm' : 'font-extrabold text-xl',
              ].join(' ')}
            >
              {server.name}
            </h3>
            <span className="text-sm text-slate-deep/60 font-semibold">
              {isVanilla ? t('Plugins · no modpack') : t('Modpack Experience')}
            </span>
          </div>
        </div>
        <ServerStatus status={server.status} />
      </div>

      <p className="text-slate-deep/80 mb-5 flex-1">{t(server.description)}</p>

      <dl className="space-y-2 mb-5 text-sm">
        <div className="flex items-center justify-between gap-2">
          <dt className="text-slate-deep/60 font-semibold">{t('Edition')}</dt>
          <dd className="font-bold">Java · {server.version}</dd>
        </div>
        <div className="flex items-center justify-between gap-2">
          <dt className="text-slate-deep/60 font-semibold">{t('Server address')}</dt>
          <dd className="font-mono font-bold text-right" data-no-translate>
            {server.host}
          </dd>
        </div>
        {server.modpack && (
          <div className="flex items-center justify-between gap-2">
            <dt className="text-slate-deep/60 font-semibold">{t('Modpack')}</dt>
            <dd className="font-bold text-right">
              {server.modpack.name} · {server.modpack.launcher}
            </dd>
          </div>
        )}
      </dl>

      <div className="mt-auto flex flex-wrap items-center gap-3">
        <LinkButton
          to="/join"
          variant={isVanilla ? 'primary' : 'sky'}
          blocky={blocky}
          size="sm"
        >
          {t('How to join')}
        </LinkButton>
        <button
          onClick={copyHost}
          className={[
            'inline-flex items-center gap-2 px-4 py-2 text-sm font-bold border-b-4 transition-all active:translate-y-[2px] active:border-b-2 shadow-block cursor-pointer',
            blocky ? 'rounded-none font-pixel text-xs' : 'rounded-xl',
            'bg-white text-slate-deep border-stone-light hover:bg-stone-light/30',
          ].join(' ')}
          aria-label={t('Copy server address')}
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
          {copied ? t('Copied') : t('Copy IP')}
        </button>
        {isVanilla ? (
          <div className="ml-auto flex flex-wrap gap-3">
            <LinkButton to="/join#commands" variant="outline" blocky={blocky} size="sm">
              {t('Commands')}
            </LinkButton>
            <LinkButton to="/mods?server=vanilla" variant="outline" blocky={blocky} size="sm">
              {t('View plugins')}
            </LinkButton>
          </div>
        ) : (
          <LinkButton
            to="/mods?server=modded"
            variant="outline"
            blocky={blocky}
            size="sm"
            className="ml-auto"
          >
            {t('View mods')}
          </LinkButton>
        )}
      </div>
    </Card>
  )
}
